import { Component, lazy, Suspense, useState, type ReactNode } from "react";
import { useMapConfig } from "../../lib/queries";
import { webglAvailable } from "../../lib/webgl";
import { Spinner } from "../../components/ui";
import GeoMap from "./GeoMap";

// Mapbox bundle is lazy-loaded so it only ships when actually rendered.
const MapboxMap = lazy(() => import("./MapboxMap"));

/** Catches a Mapbox/WebGL failure and transparently renders the SVG map instead. */
class MapFallbackBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * Chooses the best available map: Mapbox GL when a token is configured AND the
 * browser supports WebGL; otherwise (or on any Mapbox runtime failure) the
 * self-contained SVG map. The user always gets a working map.
 */
export default function MapView({ variant = "embedded" }: { variant?: "full" | "embedded" }) {
  const { data: config, isLoading } = useMapConfig();
  const [failed, setFailed] = useState(false);

  if (isLoading) return <div className="flex h-full items-center justify-center"><Spinner /></div>;

  const mapUnavailable = (
    <div className="flex h-full min-h-[200px] items-center justify-center text-sm text-slatey">
      Map unavailable in this view.
    </div>
  );

  const useMapbox = config?.has_token && webglAvailable() && !failed;

  // SVG fallback is itself wrapped so no map path can ever reach the app boundary.
  if (!useMapbox) return <MapFallbackBoundary fallback={mapUnavailable}><GeoMap /></MapFallbackBoundary>;

  return (
    <MapFallbackBoundary fallback={<MapFallbackBoundary fallback={mapUnavailable}><GeoMap /></MapFallbackBoundary>}>
      <Suspense fallback={<div className="flex h-full items-center justify-center"><Spinner label="Loading map…" /></div>}>
        <MapboxMap variant={variant} onFatal={() => setFailed(true)} />
      </Suspense>
    </MapFallbackBoundary>
  );
}
